import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  ListItemButton,
} from '@mui/material';
import {
  Event as EventIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { format, isToday, isTomorrow } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { WidgetCard } from './WidgetCard';
import { WidgetItemsModal } from './WidgetItemsModal';
import { MoreItemsBadge } from './MoreItemsBadge';
import { api } from '../../../services/api';

const MAX_VISIBLE_ITEMS = 3;

interface Booking {
  _id: string;
  clientId: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  serviceId: {
    _id: string;
    name: string;
    color?: string;
  };
  startTime: string;
  endTime: string;
  status: string;
}

export const UpcomingBookingsWidget: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['bookings', 'upcoming'],
    queryFn: async () => {
      const now = new Date().toISOString();
      const response = await api.get('/bookings', {
        params: {
          startAfter: now,
          limit: 20,
          sortBy: 'startTime',
          sortOrder: 'asc',
        },
      });
      return response.data;
    },
  });

  const bookings: Booking[] = data?.data?.bookings || [];
  const visibleBookings = bookings.slice(0, MAX_VISIBLE_ITEMS);
  const remainingCount = Math.max(0, bookings.length - MAX_VISIBLE_ITEMS);

  const formatBookingDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return `Today at ${format(date, 'h:mm a')}`;
    }
    if (isTomorrow(date)) {
      return `Tomorrow at ${format(date, 'h:mm a')}`;
    }
    return format(date, 'MMM d, h:mm a');
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  const handleBookingClick = (bookingId: string) => {
    setModalOpen(false);
    navigate(`/bookings?id=${bookingId}`);
  };

  const BookingListItem = ({ booking, inModal = false }: { booking: Booking; inModal?: boolean }) => (
    <ListItem disablePadding>
      <ListItemButton
        onClick={() => handleBookingClick(booking._id)}
        sx={{
          py: inModal ? 1.5 : 0.75,
          px: inModal ? 2 : 0,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <ListItemAvatar sx={{ minWidth: 40 }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              fontSize: '0.875rem',
              bgcolor: booking.serviceId?.color || 'primary.light',
            }}
          >
            {booking.clientId ? getInitials(booking.clientId.firstName, booking.clientId.lastName) : '?'}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {booking.clientId
                ? `${booking.clientId.firstName} ${booking.clientId.lastName}`
                : 'Unknown Client'}
            </Typography>
          }
          secondary={
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {booking.serviceId?.name || 'Service'}
              </Typography>
              <Chip
                label={formatBookingDate(booking.startTime)}
                size="small"
                color={isToday(new Date(booking.startTime)) ? 'primary' : 'default'}
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem', mt: 0.25 }}
              />
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>
  );

  const EmptyState = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100%"
      py={2}
    >
      <CalendarIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        No upcoming bookings
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Your schedule is clear
      </Typography>
    </Box>
  );

  return (
    <>
      <WidgetCard
        title="Upcoming Bookings"
        icon={<EventIcon color="info" />}
        isLoading={isLoading}
        error={error as Error | null}
        onRefresh={() => refetch()}
        viewAllLink="/calendar"
        viewAllLabel="View Calendar"
        minHeight={180}
      >
        {bookings.length === 0 ? (
          <EmptyState />
        ) : (
          <Box display="flex" flexDirection="column" height="100%">
            <List dense disablePadding sx={{ flex: 1 }}>
              {visibleBookings.map((booking) => (
                <BookingListItem key={booking._id} booking={booking} />
              ))}
            </List>
            {remainingCount > 0 && (
              <Box display="flex" justifyContent="center" pt={1}>
                <MoreItemsBadge
                  count={remainingCount}
                  onClick={() => setModalOpen(true)}
                  color="info"
                  label={`+${remainingCount} more bookings. Click to view all`}
                />
              </Box>
            )}
          </Box>
        )}
      </WidgetCard>

      <WidgetItemsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Upcoming Bookings"
        icon={<EventIcon color="info" />}
        totalCount={bookings.length}
      >
        {bookings.map((booking) => (
          <BookingListItem key={booking._id} booking={booking} inModal />
        ))}
      </WidgetItemsModal>
    </>
  );
};

export default UpcomingBookingsWidget;
